import { navigate } from '../lib/router'
import { Wordmark, MadeByApplox } from './brand/Wordmark'
import { cn } from '../lib/cn'

/**
 * Site footer (doWallet DNA: light, border-t, sitemap columns, Applox
 * credit). `variant="compact"` is the one-line strip for auth pages.
 */
export function Footer({ variant = 'site', className }: { variant?: 'site' | 'compact'; className?: string }) {
  const year = new Date().getFullYear()
  const go = (href: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(href) }

  if (variant === 'compact') {
    return (
      <footer className={cn('mt-auto w-full py-6', className)}>
        <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-3 px-6 md:flex-row">
          <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Doable</span> by Applox GmbH</p>
          <div className="flex gap-5 text-xs text-muted-foreground">
            <a href="/terms" onClick={go('/terms')} className="transition-colors hover:text-foreground">Terms</a>
            <a href="/privacy" onClick={go('/privacy')} className="transition-colors hover:text-foreground">Privacy</a>
            <a href="/impressum" onClick={go('/impressum')} className="transition-colors hover:text-foreground">Impressum</a>
          </div>
          <p className="text-xs text-muted-foreground/60">© {year} Applox GmbH</p>
        </div>
      </footer>
    )
  }

  return (
    <footer className={cn('border-t bg-background', className)}>
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="flex flex-col justify-between gap-10 md:flex-row md:items-start">
          <div className="space-y-3">
            <Wordmark />
            <p className="text-xs text-muted-foreground">An Applox GmbH product</p>
            <p className="text-xs text-muted-foreground/70">© {year} Applox GmbH. All rights reserved.</p>
          </div>
          <div className="grid grid-cols-2 gap-10 text-sm sm:grid-cols-3 sm:gap-14">
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Product</p>
              <ul className="space-y-2">
                <FooterLink href="/#features" label="Features" />
                <FooterLink href="/#security" label="Security" />
                <FooterLink href="/#pricing" label="Pricing" />
                <FooterLink href="/#demo" label="Demo" />
              </ul>
            </div>
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Legal</p>
              <ul className="space-y-2">
                <FooterLink href="/terms" label="Terms of Service" nav />
                <FooterLink href="/privacy" label="Privacy Policy" nav />
                <FooterLink href="/impressum" label="Impressum" nav />
              </ul>
            </div>
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Contact</p>
              <ul className="space-y-2">
                <li><a href="mailto:hello@doable.website" className="text-muted-foreground transition-colors hover:text-foreground">hello@doable.website</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-between">
          <MadeByApplox />
          <p className="text-xs text-muted-foreground">Hosted in Germany · GDPR-compliant · Your API key, your code</p>
        </div>
      </div>
    </footer>
  )
}

function FooterLink({ href, label, nav }: { href: string; label: string; nav?: boolean }) {
  return (
    <li>
      <a
        href={href}
        onClick={nav ? (e) => { e.preventDefault(); navigate(href) } : undefined}
        className="text-muted-foreground transition-colors hover:text-foreground"
      >
        {label}
      </a>
    </li>
  )
}
